import React, { useEffect, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { Link } from 'react-router-dom';
import NotificationCard from './NotificationCard';
import { useAuth } from './AuthContext';

const NotificationCenter = () => {
  const { token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [usersCache, setUsersCache] = useState({});

  // ✅ Charger le profil utilisateur (avec token + fallback avatar)
  const ensureProfile = useCallback(
    async (username) => {
      if (!username || usersCache[username]) return;
      try {
        const r = await fetch(`/users/${username}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error('fetch failed');
        const u = await r.json();
        setUsersCache((prev) => ({
          ...prev,
          [username]: {
            ...u,
            profilepath: u?.profilepath
              ? u.profilepath.startsWith('/uploads/')
                ? u.profilepath
                : `/uploads/${u.profilepath.replace(/^\/+/, '')}`
              : '/default-avatar.png',
          },
        }));
      } catch (e) {
        setUsersCache((prev) => ({
          ...prev,
          [username]: { username, profilepath: '/default-avatar.png' },
        }));
      }
    },
    [usersCache, token]
  );

  // ✅ Chargement initial (badge + liste persistée)
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const r = await fetch('/notifications/me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const j = await r.json();
        const items = Array.isArray(j.items) ? j.items : [];
        const uniqUsers = [
          ...new Set(
            items.flatMap((n) => [n.user_id, n.decided_by].filter(Boolean))
          ),
        ];
        await Promise.all(uniqUsers.map((u) => ensureProfile(u)));
        setNotifications(items);
        setUnread(j.unread || 0);
      } catch (e) {
        console.warn('Load notifications failed', e);
      }
    })();
  }, [token, ensureProfile]);

  // 🔔 Temps réel
  useEffect(() => {
    const socket = io('https://weavevo.com', {
      path: '/socket.io/',
      transports: ['websocket'],
      secure: true,
    });
    socket.on('notification', async (data) => {
      if (data?.user_id) await ensureProfile(data.user_id);
      if (data?.decided_by) await ensureProfile(data.decided_by);
      setNotifications((prev) => [data, ...prev]);
      setUnread((prev) => prev + 1);
    });
    return () => socket.disconnect();
  }, [ensureProfile]);

  const markAllAsRead = async () => {
    try {
      await fetch('/notifications/read-all', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
      setUnread(0);
    } catch {}
  };

  const handleDecision = async (notif, action, reason) => {
    try {
      if (action === 'read' && notif?.id) {
        await fetch(`/notifications/${notif.id}/read`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch (e) {
      console.warn('Erreur action notif', e);
    } finally {
      if (action === 'read') {
        setNotifications((prev) =>
          prev.map((n) => (n.id === notif.id ? { ...n, is_read: 1 } : n))
        );
        setUnread((u) => Math.max(0, u - 1));
      } else if (action === 'approved' || action === 'denied') {
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notif.id ? { ...n, decision: action, reason } : n
          )
        );
      }
    }
  };

  return (
    <div className="dropdown">
      <button
        className="btn position-relative"
        type="button"
        data-bs-toggle="dropdown"
        aria-expanded="false"
        onClick={markAllAsRead}
        title="Notifications"
        style={{
          background: 'transparent',
          border: 'none',
          padding: 0,
          color: '#000',
          fontSize: '1.5rem',
        }}
      >
        <i className={unread > 0 ? 'bi bi-bell-fill' : 'bi bi-bell'}></i>
        {unread > 0 && (
          <span
            className="position-absolute rounded-circle bg-danger text-white fw-bold"
            style={{
              top: '4px',
              left: '8px',
              fontSize: '0.7rem',
              minWidth: '18px',
              height: '18px',
              lineHeight: '18px',
              textAlign: 'center',
              boxShadow: '0 0 4px rgba(0,0,0,0.2)',
            }}
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      <ul
        className="dropdown-menu dropdown-menu-end p-0 shadow-sm"
        style={{
          minWidth: '360px',
          maxHeight: '440px',
          overflowY: 'auto',
          borderRadius: '12px',
          background: '#fff',
          border: '1px solid #ddd',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        }}
      >
        {/* ✅ TOP BAR (sticky) : link moved to top + header actions */}
        <li
          className="bg-white px-2 pt-2 pb-2"
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            borderBottom: '1px solid rgba(0,0,0,0.08)',
          }}
        >
          {/* 🔗 Link on top */}
          <Link
            to="/notifications"
            className="btn btn-outline-dark btn-sm w-100 rounded-pill d-inline-flex align-items-center justify-content-center gap-2 shadow-sm"
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            <i className="bi bi-arrow-up-right-square" />
            <span>Ouvrir le centre de notifications</span>
          </Link>

          {/* Header row */}
          <div className="d-flex align-items-center justify-content-between mt-2">
            <span className="fw-bold text-dark d-flex align-items-center gap-2">
              <i className="bi bi-bell" />
              <span>Notifications</span>
            </span>

            <button
              className="btn btn-sm btn-light border rounded-pill d-inline-flex align-items-center gap-2 text-nowrap"
              onClick={markAllAsRead}
              type="button"
              title="Tout marquer comme lu"
            >
              <i className="bi bi-eye" />
              <span className="d-none d-sm-inline">Tout marquer</span>
            </button>
          </div>
        </li>

        {/* BODY */}
        {notifications.length === 0 ? (
          <li className="text-muted small px-3 py-3">Aucune notification</li>
        ) : (
          <>
            {notifications.map((notif) => (
              <li
                key={
                  notif.id ||
                  `${notif.topic}-${notif.project_id}-${notif.user_id}-${notif.created_at}`
                }
                className="px-1 py-1"
              >
                <NotificationCard
                  notif={notif}
                  onDecision={handleDecision}
                  user={
                    usersCache[notif.user_id] || {
                      username: notif.user_id,
                      profilepath: '/default-avatar.png',
                    }
                  }
                />
              </li>
            ))}
          </>
        )}
      </ul>
    </div>
  );
};

export default NotificationCenter;
